<template>
  <section :class="$style.content" data-test="contact-request-email-verification-panel">
    <HgLink
      tag="button"
      type="button"
      :class="$style.backButton"
      data-test="contact-request-email-verification-back"
      @click="emit('back')"
    >
      <HgIcon icon="ArrowLeft" size="small" theme="current" />
      {{ t('emailVerificationModal.back') }}
    </HgLink>

    <div :class="$style.illustrationContainer" aria-hidden="true">
      <img v-if="illustrationSrc" :class="$style.illustration" :src="illustrationSrc" alt="" />
    </div>

    <div :class="$style.copy">
      <h2 :class="$style.title">
        {{ t('emailVerificationEnforcePanel.title') }}
      </h2>

      <p :class="$style.description" v-html="t('emailVerificationEnforcePanel.description')"></p>
    </div>

    <div :class="$style.confirmationSentTo">
      <HgIcon icon="Email" theme="secondary" size="large" :class="$style.icon" />
      <div :class="$style.pushTextRight">
        <p>{{ t('emailVerificationModal.confirmation.sent') }}</p>
        <strong>{{ email }}</strong>
      </div>
    </div>

    <div :class="$style.confirmationWarning">
      <HgIcon icon="Warning" theme="danger" size="large" :class="$style.icon" />
      <div :class="$style.pushTextRight">
        <p>{{ t('emailVerificationEnforcePanel.confirmation.warning') }}</p>
      </div>
    </div>

    <ResendEmailVerificationButton :contactRequestId="props.contactRequestId" />

    <p :class="$style.spamNote">
      {{ t('emailVerificationModal.resendNote') }}
    </p>
  </section>
</template>

<script setup lang="ts">
import { HgIcon, HgLink } from '@smg-real-estate/domus';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { isPlatformIs24 } from '@/helpers/platformDetection';
import ResendEmailVerificationButton from './ResendEmailVerificationButton.vue';

defineOptions({
  name: 'ContactRequestEmailVerificationEnforcePanel',
});

interface Props {
  email?: string;
  contactRequestId: string;
}

const props = withDefaults(defineProps<Props>(), {
  email: '',
});

const emit = defineEmits<{
  back: [];
}>();

const { t } = useI18n();

const isIs24 = isPlatformIs24();
const illustrationSrc = ref<string>();

const setIllustration = async () => {
  const importedIllustration = await import(
    isIs24
      ? '@/assets/contact-request/email-verification-mail-enforce-is24.svg?url'
      : '@/assets/contact-request/email-verification-mail-enforce-hg.svg?url'
  );

  illustrationSrc.value = importedIllustration.default;
};

setIllustration();
</script>

<style lang="scss" module>
@import '@/assets/variables';

$illustration-size: 92px;

.content {
  display: flex;
  flex-direction: column;
  gap: $space-75;
}

.backButton {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: $space-25;
  padding: 0;
  color: var(--base-color-link);
  font-size: var(--typo-body-3-font-size);
  font-weight: 700;
  line-height: var(--typo-body-3-line-height);
}

.illustrationContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: $illustration-size;
  height: $illustration-size;
  margin-top: $space-25;
}

.illustration {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-50;
}

.title {
  @include heading-3;

  max-width: 19rem;
  margin: 0;
  text-align: center;
}

.description {
  @include body-3;

  max-width: 18rem;
  margin: 0;
  text-align: center;
}

.confirmationSentTo,
.confirmationWarning {
  @include body-3;

  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: $space-50;
  align-items: start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: $space-50 $space-75;
  background: $color-grey-30;
  border-radius: $border-radius;

  .icon {
    align-self: start;
  }

  .pushTextRight {
    display: flex;
    flex-direction: column;
    gap: $space-25;
    align-items: flex-start;
    min-width: 0;

    strong {
      @include body-3-bold;
    }

    p,
    strong {
      margin: 0;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }
}

.confirmationWarning {
  background: $color-yellow-0;
}

.steps {
  @include body-3;

  padding-left: $space-100;
  margin: 0;

  li + li {
    margin-top: $space-25;
  }
}

.spamNote {
  @include body-3;

  padding-top: $space-50;
  margin: 0;
  color: $color-grey-100;
  text-align: center;
  font-size: 12px;
}
</style>
